import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { NormalizedName } from "../../shared/normalizeName";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/schema";
import type { Slug } from "../../shared/slug";
import { isRouteType, ROUTE_TYPES } from "../../shared/stepConfig";
import { useQuery } from "../hooks/useQuery";
import {
  ROUTE_TYPE_DESCRIPTIONS,
  ROUTE_TYPE_ICONS,
  ROUTE_TYPE_INLINE_LABELS,
  ROUTE_TYPE_LABELS,
} from "../lib/routeTypeDisplay";
import { makeUrl, useCurrentUrl } from "../lib/urls";
import type { RouteNode } from "../lib/urls/types";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { Icon, type IconName } from "./Icon";
import { InfiniteScroll } from "./InfiniteScroll";
import { Link } from "./Link";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
} from "./ListFilter";
import { PageCard } from "./PageCard";
import { visiblePageCardRoutes } from "./pageCardRoutes";
import { Text } from "./Text";

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`;

const SecondaryHeader = styled("div")`
  position: sticky;
  top: var(--sticky-top, 0px);
  z-index: 3;
  background: var(--surface-primary);
  border-bottom: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 12px;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const FilterPill = styled(Link)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &[data-selected="true"] {
    background: var(--alpha4);
    color: var(--text-primary);
  }
  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const SortSpacer = styled("div")`
  margin-left: auto;
`;

const FilterFooter = styled("div")`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  z-index: 1;
  background: var(--surface-primary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  min-height: 36px;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const VIEWED_SORT_OPTION = {
  key: "viewed",
  label: "Recently viewed",
  icon: "history",
} as const;

const SORT_OPTIONS = [
  { key: "updated", label: "Recently updated", icon: "sparkles" },
  { key: "name", label: "Name", icon: "arrow-down-a-z" },
] as const;

type SortKey =
  | (typeof SORT_OPTIONS)[number]["key"]
  | typeof VIEWED_SORT_OPTION.key;

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  padding: 32px;
  align-content: start;
  align-items: start;
`;

const EmptyFilterArea = styled("div")`
  flex: 1;
  min-height: 0;
`;

const PAGE_SIZE = 200;

type LoadSpaceRow = QueryRowType<ReturnType<typeof queries.loadSpace>>;
type LoadSpaceWorkflow = NonNullable<LoadSpaceRow>["workflows"][number];
// PagesList never reads commit files, so keep AppWorkflow's branches on the
// plain shape — rows from queries without the README relation still satisfy it.
type PlainBranch = NonNullable<
  QueryRowType<ReturnType<typeof queries.allWorkflowsWithLinks>>
>["branches"][number];
export type AppWorkflow = Omit<LoadSpaceWorkflow, "branches"> & {
  readonly branches: readonly PlainBranch[];
};
export type AppSpace = {
  readonly id: string;
  readonly name: NormalizedName;
  readonly slug: Slug;
  readonly mode: SpaceMode;
};
export type AppBranch = AppWorkflow["branches"][number];
export type AppRoute = NonNullable<AppBranch["headCommit"]>["routes"][number];

export type PageRow = {
  workflow: AppWorkflow;
  space: AppSpace;
  lastSeenAt?: number;
  // When set, the card shows a generic label instead of the space name, for a
  // space the viewer isn't a member of (the Links gallery).
  hideSpaceName?: boolean;
};

export { countPageCards } from "./pageCardRoutes";

type Props = {
  rows: PageRow[];
  linksRoute: RouteNode;
  showSpaceColumn: boolean;
  emptyTitle: string;
  emptyDescription: string;
  isComplete: boolean;
  recents?: boolean;
  // Favorite an individual link (route) rather than its workflow: the card's
  // heart reflects and toggles the route favorite, matching the Links gallery.
  routeFavorites?: boolean;
  // Only ever render route-favorited links (the Favorites page), with no
  // toggle. Pair with routeFavorites so unfavoriting drops the card.
  favoritesOnly?: boolean;
  filters?: FilterDef[];
};

export function PagesList({
  rows,
  linksRoute,
  showSpaceColumn,
  emptyTitle,
  emptyDescription,
  isComplete,
  recents,
  routeFavorites = false,
  favoritesOnly = false,
  filters,
}: Props) {
  const z = useZero();
  const [favoriteIds] = useQuery(queries.userFavoriteWorkflowIds());
  const favoriteSet = new Set(favoriteIds.map((f) => f.workflowId));
  const [routeFavoriteIds] = useQuery(queries.userFavoriteRouteIds());
  const routeFavoriteSet = new Set(
    routeFavoriteIds.map((f) => `${f.workflowId}:${f.stepDir}`)
  );
  const { type, sort: sortParam } = useCurrentUrl(linksRoute) as {
    type?: string;
    sort?: string;
  };
  const typeFilter = isRouteType(type) ? type : null;
  const favFilter = favoritesOnly;
  const sortOptions = recents
    ? [VIEWED_SORT_OPTION, ...SORT_OPTIONS]
    : SORT_OPTIONS;
  const defaultSort: SortKey = recents ? "viewed" : "updated";
  const sort: SortKey = sortOptions.some((o) => o.key === sortParam)
    ? (sortParam as SortKey)
    : defaultSort;

  const [limit, setLimit] = useState(PAGE_SIZE);
  // Reset the window only on the in-component controls that redefine it. The
  // upstream search/scope filters arrive through `rows`, and keying on
  // `rows.length` would reset the window (jumping the scroll) mid-sync or on any
  // unrelated workflow create/delete/move.
  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setLimit(PAGE_SIZE);
  }, [typeFilter, favFilter, sort]);

  const sortedRows = [...rows].sort((a, b) => {
    if (sort === "viewed") return (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0);
    if (sort === "name") return a.workflow.name.localeCompare(b.workflow.name);
    return b.workflow.updatedAt - a.workflow.updatedAt;
  });

  const filtersActive = filters != null && hasActiveFilters(filters);

  // Keep the subheader mounted when a filter empties the list, so its pills stay
  // reachable to clear; only bail out when there are genuinely no links.
  if (rows.length === 0 && !filtersActive) {
    return isComplete ? (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        iconName="product-cases"
      />
    ) : null;
  }

  const allRoutes = visiblePageCardRoutes(sortedRows, {
    typeFilter,
    favFilter,
    routeFavoriteSet,
  });
  const linkCards = allRoutes
    .slice(0, limit)
    .map(
      ({
        row: { workflow, space, hideSpaceName },
        branch,
        route,
        webpageRoutes,
      }) => (
        <PageCard
          key={`${workflow.id}-${branch.id}-${route.stepDir}`}
          workflow={workflow}
          branch={branch}
          route={route}
          webpageRoutes={webpageRoutes}
          space={space}
          showSpaceColumn={showSpaceColumn}
          hideSpaceName={hideSpaceName}
          isFavorite={
            routeFavorites
              ? routeFavoriteSet.has(`${workflow.id}:${route.stepDir}`)
              : favoriteSet.has(workflow.id)
          }
          onToggleFavorite={
            routeFavorites
              ? () =>
                  z.mutate(
                    sharedMutators.toggleRouteFavorite({
                      workflowId: workflow.id,
                      stepDir: route.stepDir,
                    })
                  )
              : undefined
          }
          showSelectStepAction={false}
          showOpenWorkflowAction
        />
      )
    );

  const emptyCopy: { title: string; description: string; iconName: IconName } =
    favFilter
      ? {
          title: "No favorite links",
          description: "Favorite a link to see it here.",
          iconName: "heart",
        }
      : typeFilter
        ? {
            title: `No ${ROUTE_TYPE_INLINE_LABELS[typeFilter]} links`,
            description: ROUTE_TYPE_DESCRIPTIONS[typeFilter],
            iconName: ROUTE_TYPE_ICONS[typeFilter],
          }
        : filtersActive
          ? {
              title: "No matches",
              description: "Try a different filter.",
              iconName: "product-cases",
            }
          : {
              title: emptyTitle,
              description: emptyDescription,
              iconName: "product-cases",
            };

  return (
    <Outer>
      <SecondaryHeader>
        {ROUTE_TYPES.map((t) => (
          <FilterPill
            key={t}
            to={makeUrl(linksRoute, { type: typeFilter === t ? "all" : t })}
            data-selected={typeFilter === t ? "true" : "false"}
          >
            <Icon name={ROUTE_TYPE_ICONS[t]} size={16} />
            <Text variant="text-small-md">{ROUTE_TYPE_LABELS[t]}</Text>
          </FilterPill>
        ))}
        <SortSpacer />
        <Dropdown
          align="bottom"
          minWidth={180}
          opener={
            <Button
              variant="secondary"
              icon="arrow-down-up"
              aria-label="Sort"
            />
          }
          sections={[
            {
              items: sortOptions.map((o) => ({
                type: "link" as const,
                key: o.key,
                to: makeUrl(linksRoute, {
                  sort: o.key === defaultSort ? "" : o.key,
                }),
                left: <Icon name={o.icon} size={16} />,
                label: o.label,
                right: (
                  <Icon
                    name="check"
                    size={12}
                    color={
                      sort === o.key ? "var(--text-secondary)" : "transparent"
                    }
                  />
                ),
              })),
            },
          ]}
        />
        {filters && <ListFilter filters={filters} />}
      </SecondaryHeader>
      {allRoutes.length === 0 && isComplete ? (
        <EmptyFilterArea>
          <EmptyState
            title={emptyCopy.title}
            description={emptyCopy.description}
            iconName={emptyCopy.iconName}
          />
        </EmptyFilterArea>
      ) : (
        <Grid>
          {linkCards}
          {allRoutes.length > limit && (
            <div style={{ gridColumn: "1 / -1" }}>
              <InfiniteScroll
                hasMore={allRoutes.length > limit}
                onLoadMore={() => setLimit((p) => p + PAGE_SIZE)}
              />
            </div>
          )}
        </Grid>
      )}
      {filtersActive && (
        <FilterFooter>
          <ListFilterPills filters={filters} />
        </FilterFooter>
      )}
    </Outer>
  );
}
